// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the row every Konvo card in a chat starts with (design/assistant-chat-unify): a 28px icon
// tile, what the card is, an optional detail, then its status and the message's time at the end.
// Konvo's messages show no time of their own in the Assistant chat, so the card carries it. The
// card's ⋯ (AssistantCardMenu) seats itself at the end of this row.

import {useMaybeMessageViewContext} from '@app/features/channel/components/MessageViewContext';
import styles from '@app/features/konvo_assistant/components/card/KonvoChatCard.module.css';
import {clsx} from 'clsx';
import type React from 'react';

// Plain Intl, like the cards' other times: the card stays light (no stores), so it renders anywhere.
const CLOCK_TIME = new Intl.DateTimeFormat(undefined, {hour: 'numeric', minute: '2-digit'});
const FULL_TIME = new Intl.DateTimeFormat(undefined, {dateStyle: 'full', timeStyle: 'short'});

/** A time in a card header ("8:10"), for cards whose time is not their message's own. */
export function cardClockTime(at: Date | number): string {
	return CLOCK_TIME.format(at);
}

interface KonvoCardHeadProps {
	icon: React.ReactNode;
	label: React.ReactNode;
	meta?: React.ReactNode;
	/** Status, counts or an action, before the time. */
	end?: React.ReactNode;
	/** Show the message's time at the end (default). */
	time?: boolean;
	className?: string;
}

export function KonvoCardHead({icon, label, meta, end, time = true, className}: KonvoCardHeadProps) {
	const timestamp = useMaybeMessageViewContext()?.message.timestamp;
	const shownTime = time ? timestamp : undefined;
	return (
		<header className={clsx(styles.head, className)} data-flx="konvo-assistant.card-head">
			<span className={styles.tile} aria-hidden="true">
				{icon}
			</span>
			<span className={styles.label}>{label}</span>
			{meta != null && meta !== false && <span className={styles.meta}>{meta}</span>}
			{((end != null && end !== false) || shownTime) && (
				<span className={styles.end}>
					{end != null && end !== false && <span className={styles.endPart}>{end}</span>}
					{shownTime && (
						<time
							className={styles.time}
							dateTime={shownTime.toISOString()}
							title={FULL_TIME.format(shownTime)}
							data-message-copy-hidden="true"
						>
							{CLOCK_TIME.format(shownTime)}
						</time>
					)}
				</span>
			)}
		</header>
	);
}
